/**
 * Catalog list view with filter form. Calls `GET /cards` with the
 * supplied filters and renders the current page of {@link CardRecord}s.
 *
 * Validates: Requirements 5.1, 5.2
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatIdentity, IdentityStatusBadge } from '../shared/PredictionView';
import { normalizeShortCode, type CardRecord } from '../shared/types';
import { useCatalogApi, type CatalogFilters, type CatalogListResponse } from './catalog-api';

const CARD_SEARCH_DEFAULT_LIMIT = 5;
const CARD_SEARCH_LIMIT_OPTIONS = [5, 10, 25, 50] as const;

export function CatalogListPage(): JSX.Element {
  return <CardSearchPanel title="Cards" showShortCodeLookup />;
}

export function CardSearchPanel({
  title,
  showShortCodeLookup = false,
  showFilters = true,
  showSkuFilter = true,
  intro,
}: {
  title?: string;
  showShortCodeLookup?: boolean;
  showFilters?: boolean;
  showSkuFilter?: boolean;
  intro?: string;
}): JSX.Element {
  const catalog = useCatalogApi();
  const location = useLocation();
  const restoreKey = cardSearchRestoreKey(location.pathname, location.search, title);
  const [restored] = useState(() => readCardSearchRestore(restoreKey));
  const [filters, setFilters] = useState<CatalogFilters>(
    restored?.filters ?? { limit: CARD_SEARCH_DEFAULT_LIMIT },
  );
  const [page, setPage] = useState<CatalogListResponse | null>(restored?.page ?? null);
  const latestRestore = useRef<{ filters: CatalogFilters; page: CatalogListResponse | null }>({
    filters: restored?.filters ?? { limit: CARD_SEARCH_DEFAULT_LIMIT },
    page: restored?.page ?? null,
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const persistRestore = useCallback(
    (scrollY = window.scrollY) => {
      writeCardSearchRestore(restoreKey, { ...latestRestore.current, scrollY });
    },
    [restoreKey],
  );

  useEffect(() => {
    latestRestore.current = { filters, page };
  }, [filters, page]);

  const fetchPage = useCallback(
    async (filtersOverride?: CatalogFilters) => {
      const f = filtersOverride ?? filters;
      setLoading(true);
      setError(null);
      const result = await catalog.list(f);
      setLoading(false);
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setPage(result.data);
    },
    [catalog, filters],
  );

  useEffect(() => {
    if (restored?.scrollY === undefined || page === null) return;
    const id = window.setTimeout(() => window.scrollTo({ top: restored.scrollY }), 0);
    return () => window.clearTimeout(id);
  }, [page, restored]);

  useEffect(() => {
    return () => {
      persistRestore();
    };
  }, [persistRestore]);

  const onApply = useCallback(
    (next: CatalogFilters) => {
      setFilters(next);
      void fetchPage(next);
    },
    [fetchPage],
  );

  return (
    <div>
      {title !== undefined ? <h2>{title}</h2> : null}
      {intro !== undefined ? <p className="tcvs-caption">{intro}</p> : null}
      {showShortCodeLookup ? <ShortCodeLookup /> : null}
      {showFilters ? (
        <FilterForm value={filters} onApply={onApply} showSkuFilter={showSkuFilter} />
      ) : null}
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {loading ? <p>Loading…</p> : null}
      {page !== null ? <ResultsTable items={page.items} onBeforeOpen={persistRestore} /> : null}
      {page === null && !loading ? (
        <p className="tcvs-muted">Search to display matching cards.</p>
      ) : null}
      {page !== null && page.lastKey !== null ? (
        <button
          type="button"
          onClick={() => onApply({ ...filters, lastKey: page.lastKey ?? undefined })}
        >
          Next page
        </button>
      ) : null}
    </div>
  );
}

const CARD_SEARCH_RESTORE_PREFIX = 'tcvs.cardSearchRestore.';

interface CardSearchRestoreState {
  filters: CatalogFilters;
  page: CatalogListResponse | null;
  scrollY: number;
}

function cardSearchRestoreKey(pathname: string, search: string, title: string | undefined): string {
  return `${CARD_SEARCH_RESTORE_PREFIX}${pathname}${search}#${title ?? 'cards'}`;
}

function readCardSearchRestore(key: string): CardSearchRestoreState | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<CardSearchRestoreState>;
    if (parsed.filters === undefined || parsed.page === undefined) return null;
    return {
      filters: parsed.filters,
      page: parsed.page,
      scrollY: typeof parsed.scrollY === 'number' ? parsed.scrollY : 0,
    };
  } catch {
    return null;
  }
}

function writeCardSearchRestore(key: string, state: CardSearchRestoreState): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Best-effort navigation restore only; storage can be unavailable in private modes.
  }
}

interface FilterFormProps {
  value: CatalogFilters;
  onApply: (next: CatalogFilters) => void;
  showSkuFilter: boolean;
}

/**
 * Short-code lookup box. Independent of the list filters: an operator types a
 * short code (in any case / with confusable characters), it is normalized to
 * Canonical_Form, and resolved via `GET /cards?shortCode=`. On a hit we
 * navigate straight to the card detail page. On a miss/malformed code we show
 * a not-found indication and LEAVE the entered input unchanged so the operator
 * can correct it (Requirements 8.1–8.4).
 */
function ShortCodeLookup(): JSX.Element {
  const catalog = useCatalogApi();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const normalized = normalizeShortCode(input);
      setLoading(true);
      setError(null);
      const result = await catalog.lookupByShortCode(normalized);
      setLoading(false);
      if (!result.ok) {
        // Leave `input` unchanged so the operator can fix the entered code.
        setError(result.message ?? result.error ?? 'No card matches that short code.');
        return;
      }
      navigate(`/catalog/${result.data.cardId}`);
    },
    [catalog, input, navigate],
  );

  return (
    <form
      style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}
      onSubmit={onSubmit}
    >
      <label>
        Short code lookup{' '}
        <input
          type="text"
          value={input}
          placeholder="e.g. K7M4QX"
          className="tcvs-mono"
          onChange={(e) => setInput(e.target.value)}
        />
      </label>
      <button type="submit" disabled={loading}>
        {loading ? 'Looking up…' : 'Look up'}
      </button>
      {error !== null ? <span className="tcvs-error">{error}</span> : null}
    </form>
  );
}

function FilterForm({ value, onApply, showSkuFilter }: FilterFormProps): JSX.Element {
  const [draft, setDraft] = useState<CatalogFilters>(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <form
      style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        // Drop any pagination cursor when filters change.
        const { lastKey: _drop, ...rest } = draft;
        void _drop;
        onApply({ ...rest, limit: draft.limit ?? CARD_SEARCH_DEFAULT_LIMIT });
      }}
    >
      <label>
        Search{' '}
        <input
          type="text"
          value={draft.q ?? ''}
          placeholder="Player, team, set, SKU, short code..."
          onChange={(e) => setDraft({ ...draft, q: e.target.value || undefined })}
        />
      </label>
      {showSkuFilter ? (
        <label>
          SKU{' '}
          <input
            type="text"
            value={draft.sku ?? ''}
            onChange={(e) => setDraft({ ...draft, sku: e.target.value || undefined })}
          />
        </label>
      ) : null}
      <label>
        Results{' '}
        <select
          value={draft.limit ?? CARD_SEARCH_DEFAULT_LIMIT}
          onChange={(e) =>
            setDraft({
              ...draft,
              limit: Number.parseInt(e.target.value, 10),
              lastKey: undefined,
            })
          }
        >
          {CARD_SEARCH_LIMIT_OPTIONS.map((limit) => (
            <option key={limit} value={limit}>
              {limit}
            </option>
          ))}
        </select>
      </label>
      <button type="submit">Apply</button>
    </form>
  );
}

function ResultsTable({
  items,
  onBeforeOpen,
}: {
  items: readonly CardRecord[];
  onBeforeOpen: () => void;
}): JSX.Element {
  // Created-column sort: null = natural (server) order; clicking the header
  // cycles desc (newest first) → asc → desc. Pure client-side view transform
  // over the loaded page; it never re-queries.
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const onToggleSort = useCallback(() => {
    setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  if (items.length === 0) return <p>No cards match the current filters.</p>;

  const rows = sortDir === null ? items : sortCardsByCreated(items, sortDir);

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th style={cellStyle}>Status</th>
          <th style={cellStyle}>Card ID</th>
          <th style={cellStyle}>Short code</th>
          <th style={cellStyle}>SKU</th>
          <th style={cellStyle}>Identity</th>
          <th
            style={{ ...cellStyle, cursor: 'pointer', userSelect: 'none' }}
            onClick={onToggleSort}
            title="Sort by creation date"
            aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}
          >
            Created{sortDir === 'asc' ? ' ▲' : sortDir === 'desc' ? ' ▼' : ' ⇅'}
          </th>
          <th style={cellStyle}>Source</th>
          <th style={cellStyle}></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((card) => (
          <tr key={card.cardId}>
            <td style={cellStyle}>
              <IdentityStatusBadge status={card.identityStatus} />
            </td>
            <td style={cellStyle}>
              <CardIdCell cardId={card.cardId} />
            </td>
            <td style={cellStyle}>
              <ShortCodeCell card={card} />
            </td>
            <td style={cellStyle}>
              <SkuCell card={card} />
            </td>
            <td style={cellStyle}>{identityCell(card)}</td>
            <td style={cellStyle}>{card.createdAt}</td>
            <td style={cellStyle}>{card.source}</td>
            <td style={cellStyle}>
              <Link to={`/catalog/${card.cardId}`} onClick={onBeforeOpen}>
                Open
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Order cards by their `createdAt` ISO timestamp without mutating the input.
 * `asc` = oldest first, `desc` = newest first. Cards with a missing/unparseable
 * `createdAt` sort to the end in both directions; equal timestamps keep their
 * incoming order, so the sort is stable.
 */
function sortCardsByCreated(items: readonly CardRecord[], direction: 'asc' | 'desc'): CardRecord[] {
  const factor = direction === 'asc' ? 1 : -1;
  const ms = (c: CardRecord): number | undefined => {
    const t = Date.parse(c.createdAt);
    return Number.isNaN(t) ? undefined : t;
  };
  return items
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const at = ms(a.card);
      const bt = ms(b.card);
      if (at === undefined && bt === undefined) return a.index - b.index;
      if (at === undefined) return 1;
      if (bt === undefined) return -1;
      if (at !== bt) return (at - bt) * factor;
      return a.index - b.index;
    })
    .map(({ card }) => card);
}

/**
 * Render a value as a monospace, click-to-copy cell so an operator can grab it
 * to paste elsewhere without digging it out of a detail-page URL. Copy uses the
 * async Clipboard API when available and falls back gracefully; a brief
 * "Copied" affordance confirms the action. The value is shown in full so it can
 * be selected manually.
 */
function CopyCell({ value, title }: { value: string; title: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(() => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    };
    if (navigator.clipboard?.writeText !== undefined) {
      navigator.clipboard.writeText(value).then(done, () => {
        /* clipboard denied — the value is still visible for manual selection */
      });
    } else {
      done();
    }
  }, [value]);
  return (
    <button
      type="button"
      onClick={onCopy}
      title={title}
      className="tcvs-mono"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left',
      }}
    >
      {value} {copied ? <span className="tcvs-tone-stored">✓ copied</span> : '📋'}
    </button>
  );
}

/** Render a card's `cardId` as a click-to-copy cell. */
function CardIdCell({ cardId }: { cardId: string }): JSX.Element {
  return <CopyCell value={cardId} title="Copy card ID" />;
}

/**
 * Render a card's short code in Canonical_Form (the server already returns it
 * canonical). Legacy cards have no `shortCode`: fall back to the `cardId`, and
 * only when `cardId` is also absent show a single hyphen placeholder. Never
 * crashes on legacy/partial cards (Requirements 9.2, 9.3, 8.5, 8.6).
 */
function ShortCodeCell({ card }: { card: CardRecord }): JSX.Element {
  if (card.shortCode !== undefined && card.shortCode !== '') {
    return <CopyCell value={card.shortCode} title="Copy short code" />;
  }
  if (card.cardId !== undefined && card.cardId !== '') {
    return <CopyCell value={card.cardId} title="Copy card ID" />;
  }
  return <span className="tcvs-muted">-</span>;
}

/**
 * Render a card's external listing-service SKU (sku-write-back), click-to-copy
 * like the short code. Cards with no SKU show a muted placeholder.
 */
function SkuCell({ card }: { card: CardRecord }): JSX.Element {
  if (card.sku !== undefined && card.sku !== '') {
    return <CopyCell value={card.sku} title="Copy SKU" />;
  }
  return <span className="tcvs-muted">-</span>;
}

/**
 * Render the authoritative identity only when the card is `IDENTIFIED`. While
 * `UNIDENTIFIED`, surface the suggested identity labeled as a suggestion rather
 * than presenting it as the card's verified identity (Requirements 7.1, 7.4).
 */
function identityCell(card: CardRecord): JSX.Element {
  if (card.identityStatus === 'IDENTIFIED' && card.identity !== undefined) {
    return <>{formatIdentity(card.identity)}</>;
  }
  const suggestion = card.suggestion?.identification;
  if (suggestion !== undefined && suggestion.status === 'known') {
    return <span className="tcvs-muted">Suggested: {formatIdentity(suggestion.identity)}</span>;
  }
  return <span className="tcvs-muted">—</span>;
}

const cellStyle: React.CSSProperties = {
  border: '1px solid var(--tcvs-border)',
  padding: '4px 8px',
  textAlign: 'left',
};
